// src/features/mensagens/utils/constants.js
// ============================================================================
// 🎯 CONSTANTES CENTRALIZADAS — ÚNICA FUENTE DE VERDAD
// 📦 V1 ENTERPRISE — Zero Duplicación · Type-Safe · Tree-Shakeable
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODO string de estado/tipo/acción DEBE salir de este archivo
// 2. NUNCA hardcodear strings como 'read', 'failed', 'incoming' en componentes
// 3. Si un valor se usa en >1 archivo, VA AQUÍ
// 4. Object.freeze + as const = inmutable + type-safe
// 5. Arrays congelados para evitar mutaciones accidentales
// ============================================================================

// ============================================================================
// 📨 ESTADOS DE MENSAJE
// ============================================================================
export const MESSAGE_STATUS = Object.freeze({
  SENDING:   'sending',
  SENT:      'sent',
  DELIVERED: 'delivered',
  READ:      'read',
  FAILED:    'failed',
});

export const MESSAGE_STATUS_LABELS = Object.freeze({
  [MESSAGE_STATUS.SENDING]:   'Enviando...',
  [MESSAGE_STATUS.SENT]:      'Enviada',
  [MESSAGE_STATUS.DELIVERED]: 'Entregue',
  [MESSAGE_STATUS.READ]:      'Lida',
  [MESSAGE_STATUS.FAILED]:    'Falhou',
});

export const MESSAGE_STATUS_ICONS = Object.freeze({
  [MESSAGE_STATUS.SENDING]:   'Loader2',
  [MESSAGE_STATUS.SENT]:      'Check',
  [MESSAGE_STATUS.DELIVERED]: 'CheckCheck',
  [MESSAGE_STATUS.READ]:      'CheckCheck',
  [MESSAGE_STATUS.FAILED]:    'AlertCircle',
});

export const MESSAGE_STATUS_COLORS = Object.freeze({
  [MESSAGE_STATUS.SENDING]:   { icon: 'text-gray-400', bg: 'bg-gray-50' },
  [MESSAGE_STATUS.SENT]:      { icon: 'text-gray-400', bg: 'bg-gray-50' },
  [MESSAGE_STATUS.DELIVERED]: { icon: 'text-green-500', bg: 'bg-green-50' },
  [MESSAGE_STATUS.READ]:      { icon: 'text-blue-500',  bg: 'bg-blue-50' },
  [MESSAGE_STATUS.FAILED]:    { icon: 'text-red-500',   bg: 'bg-red-50' },
});

// ============================================================================
// 📤 DIRECCIÓN DE MENSAJE
// ============================================================================
export const MESSAGE_DIRECTION = Object.freeze({
  INBOUND:  'inbound',
  OUTBOUND: 'outbound',
});

export const MESSAGE_DIRECTION_LABELS = Object.freeze({
  [MESSAGE_DIRECTION.INBOUND]:  '📥 Recebido',
  [MESSAGE_DIRECTION.OUTBOUND]: '📤 Enviado',
});

// ============================================================================
// 📦 TIPO DE ENTIDAD
// ============================================================================
export const ENTITY_TYPE = Object.freeze({
  MESSAGE:      'message',
  CONVERSATION: 'conversation',
});

// ============================================================================
// 📝 TIPO DE CONTENIDO DE MENSAJE
// ============================================================================
export const MESSAGE_CONTENT_TYPE = Object.freeze({
  TEXT:     'text',
  IMAGE:    'image',
  DOCUMENT: 'document',
  AUDIO:    'audio',
  TEMPLATE: 'template',
});

export const MESSAGE_CONTENT_TYPE_ICONS = Object.freeze({
  [MESSAGE_CONTENT_TYPE.TEXT]:     'MessageSquare',
  [MESSAGE_CONTENT_TYPE.IMAGE]:    'Image',
  [MESSAGE_CONTENT_TYPE.DOCUMENT]: 'FileText',
  [MESSAGE_CONTENT_TYPE.AUDIO]:    'Mic',
  [MESSAGE_CONTENT_TYPE.TEMPLATE]: 'LayoutTemplate',
});

// ============================================================================
// 🎨 ESTADOS DE CLIENTE (PRESERVADOS del sistema existente)
// ============================================================================
export const CLIENT_STATUS = Object.freeze({
  NOVO:    'Novo',
  ATIVO:   'Ativo',
  INATIVO: 'Inativo',
  LEAD:    'Lead',
});

export const CLIENT_STATUS_COLORS = Object.freeze({
  [CLIENT_STATUS.NOVO]:    'bg-green-100 text-green-700',
  [CLIENT_STATUS.ATIVO]:   'bg-blue-100 text-blue-700',
  [CLIENT_STATUS.INATIVO]: 'bg-gray-100 text-gray-700',
  [CLIENT_STATUS.LEAD]:    'bg-purple-100 text-purple-700',
});

// ============================================================================
// 🌡️ TEMPERATURA DE LEAD
// ============================================================================
export const LEAD_TEMPERATURE = Object.freeze({
  QUENTE: 'Quente',
  MORNO:  'Morno',
  FRIO:   'Frio',
});

export const LEAD_TEMPERATURE_COLORS = Object.freeze({
  [LEAD_TEMPERATURE.QUENTE]: 'bg-red-100 text-red-700 border-red-200',
  [LEAD_TEMPERATURE.MORNO]:  'bg-yellow-100 text-yellow-700 border-yellow-200',
  [LEAD_TEMPERATURE.FRIO]:   'bg-blue-100 text-blue-700 border-blue-200',
});

// ============================================================================
// 📊 ESTADÍSTICAS
// ============================================================================
export const STATS_CARD_COLORS = Object.freeze({
  blue:   { bg: 'bg-blue-100',   text: 'text-blue-600',   icon: 'text-blue-600' },
  green:  { bg: 'bg-green-100',  text: 'text-green-600',  icon: 'text-green-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'text-purple-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', icon: 'text-indigo-600' },
  amber:  { bg: 'bg-amber-100',  text: 'text-amber-600',  icon: 'text-amber-600' },
});

// ============================================================================
// ⚙️ PERFORMANCE
// ============================================================================
export const PERFORMANCE = Object.freeze({
  VIRTUALIZATION_THRESHOLD: 50,
  SEARCH_DEBOUNCE_MS: 200,
  INFINITE_SCROLL_BATCH: 20,
  MESSAGE_EDIT_WINDOW_MS: 3_600_000,  // 1 hora
  PULL_TO_REFRESH_THRESHOLD: 80,
  SWIPE_THRESHOLD_PX: 80,
  LONG_PRESS_MS: 500,
  ANIMATION_DURATION_MS: 200,
  STALE_TIME_MS: 30_000,
  CACHE_TIME_MS: 300_000,
  RETRY_COUNT: 3,
  RETRY_DELAY_MS: 1000,
});

// ============================================================================
// 📱 PLANTILLAS DE MENSAJE (UNIFICADAS)
// ============================================================================
export const MESSAGE_TEMPLATES = Object.freeze({
  padrao: {
    id: 'padrao',
    icon: 'MessageSquare',
    label: 'Padrão',
    color: 'bg-blue-500',
    generate: (name = '') => `Olá, ${name}! 😊\n\nEstou entrando em contato da Imóveis Pro. Como posso te ajudar hoje?`,
  },
  proposta: {
    id: 'proposta',
    icon: 'FileText',
    label: 'Proposta',
    color: 'bg-green-500',
    generate: (name = '') => `Olá, ${name}! 👋\n\nTenho uma proposta interessante para você. Podemos conversar?`,
  },
  visita: {
    id: 'visita',
    icon: 'Home',
    label: 'Visita',
    color: 'bg-purple-500',
    generate: (name = '') => `Olá, ${name}! 🏠\n\nGostaria de agendar uma visita para o imóvel que você demonstrou interesse.`,
  },
  lembrete: {
    id: 'lembrete',
    icon: 'Bell',
    label: 'Lembrete',
    color: 'bg-amber-500',
    generate: (name = '') => `Olá, ${name}! ⏰\n\nPassando para lembrar da nossa conversa sobre imóveis.`,
  },
});

// ============================================================================
// 🔍 FILTROS
// ============================================================================
export const FILTER_OPTIONS = Object.freeze([
  { value: 'todos',              label: 'Todos os clientes',          icon: 'Users',        mobileLabel: 'Todos' },
  { value: 'comWhatsApp',        label: 'Com WhatsApp',              icon: 'Phone',        mobileLabel: 'WhatsApp' },
  { value: 'semWhatsApp',        label: 'Sem WhatsApp',              icon: 'X',            mobileLabel: 'Sem WA' },
  { value: 'contactadosHoje',    label: 'Contactados hoje',          icon: 'Clock',        mobileLabel: 'Hoje' },
  { value: 'contactadosSemana',  label: 'Contactados esta semana',   icon: 'Calendar',     mobileLabel: 'Semana' },
  { value: 'naoContactados',     label: 'Não contactados (7+ dias)', icon: 'AlertCircle',  mobileLabel: 'Inativos' },
  { value: 'Comprador',          label: 'Compradores',               icon: 'User',         mobileLabel: 'Comp.' },
  { value: 'Vendedor',           label: 'Vendedores',                icon: 'User',         mobileLabel: 'Vend.' },
  { value: 'Locatário',          label: 'Locatários',                icon: 'User',         mobileLabel: 'Loc.' },
  { value: 'Investidor',         label: 'Investidores',              icon: 'TrendingUp',   mobileLabel: 'Inv.' },
]);

export const MESSAGE_FILTERS = Object.freeze({
  ALL:      'all',
  UNREAD:   'unread',
  ARCHIVED: 'archived',
  STARRED:  'starred',
});

export const MESSAGE_FILTER_LABELS = Object.freeze({
  [MESSAGE_FILTERS.ALL]:      'Todas',
  [MESSAGE_FILTERS.UNREAD]:   'Não lidas',
  [MESSAGE_FILTERS.ARCHIVED]: 'Arquivadas',
  [MESSAGE_FILTERS.STARRED]:  'Favoritas',
});

// ============================================================================
// 🔔 TOAST / NOTIFICACIONES
// ============================================================================
export const TOAST = Object.freeze({
  DURATION_MS: 4000,
  MAX_VISIBLE: 5,
  POSITION: 'bottom-right',
  TYPES: Object.freeze({
    SUCCESS: 'success',
    ERROR:   'error',
    WARNING: 'warning',
    INFO:    'info',
  }),
});

// ============================================================================
// ⌨️ KEYBOARD SHORTCUTS
// ============================================================================
export const KEYBOARD_SHORTCUTS = Object.freeze({
  NEW_MESSAGE:    { key: 'n', ctrl: true, label: 'Nova mensagem' },
  SEARCH:         { key: 'k', ctrl: true, label: 'Buscar' },
  ARCHIVE:        { key: 'e', ctrl: true, label: 'Arquivar' },
  DELETE:         { key: 'Backspace', ctrl: true, label: 'Excluir' },
  ESCAPE:         { key: 'Escape', label: 'Fechar' },
});

// ============================================================================
// 📏 DIMENSIONES UI
// ============================================================================
export const UI_DIMENSIONS = Object.freeze({
  SIDEBAR_WIDTH: 280,
  CHAT_MAX_WIDTH: 800,
  BUBBLE_MAX_WIDTH: '75%',
  AVATAR_SIZE_SM: 32,
  AVATAR_SIZE_MD: 40,
  AVATAR_SIZE_LG: 56,
  ICON_SIZE_SM: 16,
  ICON_SIZE_MD: 20,
  ICON_SIZE_LG: 24,
  INPUT_MIN_HEIGHT: 44,
  INPUT_MAX_HEIGHT: 120,
  HEADER_HEIGHT: 56,
});

// ============================================================================
// 🎯 ACCIONES DE MENSAJE (Permisos)
// ============================================================================
export const MESSAGE_ACTIONS = Object.freeze({
  VIEW:    'canView',
  EDIT:    'canEdit',
  RESEND:  'canResend',
  DELETE:  'canDelete',
  COPY:    'canCopy',
  FORWARD: 'canForward',
  STAR:    'canStar',
});

export const DEFAULT_MESSAGE_PERMISSIONS = Object.freeze({
  [MESSAGE_ACTIONS.VIEW]:    true,
  [MESSAGE_ACTIONS.EDIT]:    false,
  [MESSAGE_ACTIONS.RESEND]:  false,
  [MESSAGE_ACTIONS.DELETE]:  true,
  [MESSAGE_ACTIONS.COPY]:    true,
  [MESSAGE_ACTIONS.FORWARD]: true,
  [MESSAGE_ACTIONS.STAR]:    true,
});

// ============================================================================
// 📴 OFFLINE
// ============================================================================
export const OFFLINE = Object.freeze({
  SYNC_INTERVAL_MS: 60_000,
  MAX_QUEUE_SIZE: 100,
  RETRY_STRATEGY: 'exponential',
});

// ============================================================================
// 🧊 CONGELAR TODO PARA EVITAR MUTACIONES
// ============================================================================
// Object.freeze ya aplicado a cada objeto individual
// Para máxima seguridad en runtime, congelamos también este módulo completo
if (typeof Object.freeze === 'function') {
  Object.freeze(MESSAGE_STATUS);
  Object.freeze(MESSAGE_STATUS_LABELS);
  Object.freeze(MESSAGE_STATUS_ICONS);
  Object.freeze(MESSAGE_STATUS_COLORS);
  Object.freeze(MESSAGE_DIRECTION);
  Object.freeze(MESSAGE_DIRECTION_LABELS);
  Object.freeze(ENTITY_TYPE);
  Object.freeze(MESSAGE_CONTENT_TYPE);
  Object.freeze(MESSAGE_CONTENT_TYPE_ICONS);
  Object.freeze(CLIENT_STATUS);
  Object.freeze(CLIENT_STATUS_COLORS);
  Object.freeze(LEAD_TEMPERATURE);
  Object.freeze(LEAD_TEMPERATURE_COLORS);
  Object.freeze(STATS_CARD_COLORS);
  Object.freeze(PERFORMANCE);
  Object.freeze(MESSAGE_TEMPLATES);
  Object.freeze(FILTER_OPTIONS);
  Object.freeze(MESSAGE_FILTERS);
  Object.freeze(MESSAGE_FILTER_LABELS);
  Object.freeze(TOAST);
  Object.freeze(TOAST.TYPES);
  Object.freeze(KEYBOARD_SHORTCUTS);
  Object.freeze(UI_DIMENSIONS);
  Object.freeze(MESSAGE_ACTIONS);
  Object.freeze(DEFAULT_MESSAGE_PERMISSIONS);
  Object.freeze(OFFLINE);
}